import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { IconComponent } from '@fc-shared/ui/icon/icon.component';
import { QuickSearchWorkflowItem } from '@fc-search/models/quick-search';
import { QuickSearchFacadeService } from '@fc-search/services/quick-search-facade.service';
import { ButtonComponent } from '@fc-shared/ui/buttons/button.component';
import { CopyToClipDirective } from '@fc-shared/directives/copy-to-clip.directive';
import { TooltipDirective } from '@fc-shared/ui/tooltips/tooltip.directive';
import { buildShareableAppUrl } from '@fc-core/utils/app-url';

@Component({
  selector: 'fc-qs-workflow-result',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    IconComponent,
    ButtonComponent,
    CopyToClipDirective,
    TooltipDirective,
  ],
  template: `
    <div
      class="flex gap-12 items-center max-w-full relative py-8 pl-14 pr-16 hover:bg-secondary-50 cursor-pointer group border-l-2"
      [class.border-white]="!selected()"
      [class.border-primary-600]="selected()"
      [class.bg-secondary-50]="selected()"
      [routerLink]="['/tasks/kanban', result().obj.id]"
    >
      <div
        class="size-40 shrink-0 rounded-xl bg-secondary-100 flex items-center justify-center"
      >
        <fc-icon icon="icon-status" color="secondary600" />
      </div>

      <div class="flex flex-col gap-4 grow min-w-0">
        <span class="text-sm font-medium text-grey-900 truncate">
          {{ result().obj.name }}
        </span>
        <span class="text-xs font-medium text-secondary-500">
          {{ result().obj.description }}
        </span>
      </div>

      <div
        class="flex gap-4 p-4 rounded-xl bg-white border-secondary-100 shadow-sm"
        [class.opacity-0]="!selected()"
        [class.opacity-100]="selected()"
        [class.group-hover:opacity-100]="!selected()"
      >
        <div
          fcTooltip="Copy link"
          [showTooltip]="selected() && activeButtonIndex() === 0"
        >
          <fc-button
            variant="text"
            color="secondary"
            fcCopyToClip
            [text]="link()"
            [focused]="activeButtonIndex() === 0"
            icon="icon-copy-link"
            size="sm"
            iconPosition="only"
            (click)="$event.stopPropagation()"
          />
        </div>
        <div
          fcTooltip="Open in new tab"
          [showTooltip]="selected() && activeButtonIndex() === 1"
        >
          <fc-button
            variant="filled"
            color="primary"
            [focused]="activeButtonIndex() === 1"
            icon="icon-new-window"
            size="sm"
            iconPosition="only"
            (click)="viewDetails(); $event.stopPropagation()"
          />
        </div>
      </div>
    </div>
  `,
})
export class QsWorkflowResultComponent {
  result = input.required<QuickSearchWorkflowItem>();
  selected = input(false);
  activeButtonIndex = input(-1);
  quickAction = output();
  private router = inject(Router);
  private facade = inject(QuickSearchFacadeService);

  link = computed(() => {
    const path = this.router.serializeUrl(
      this.router.createUrlTree([`tasks/kanban/${this.result().obj.id}`]),
    );
    return buildShareableAppUrl(path);
  });

  viewDetails(): void {
    this.quickAction.emit();
    window.open(this.link(), '_blank');
    this.facade.clearAndClose();
  }

  activateButton(index: number): void {
    switch (index) {
      case 0:
        navigator.clipboard.writeText(this.link());
        break;
      case 1:
        this.viewDetails();
        break;
    }
  }
}
